import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { Component, ErrorInfo, ReactNode } from 'react';
import { LanguageProvider } from './contexts/LanguageContext';
import { ClientProvider } from './contexts/ClientContext';
import { ScenarioProvider } from './contexts/ScenarioContext';

// Error Boundary to catch React errors
class ErrorBoundary extends Component<{ children: ReactNode }, { hasError: boolean; error: Error | null }> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('React Error Boundary caught an error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-surface-900 flex items-center justify-center p-8">
          <div className="max-w-2xl w-full bg-surface-800 rounded-lg p-8 border border-red-500/30">
            <h1 className="text-2xl font-bold text-red-400 mb-4">Something went wrong</h1>
            <pre className="bg-surface-700 p-4 rounded text-sm text-text-secondary overflow-auto max-h-64">
              {this.state.error?.message}
              {'\n\n'}
              {this.state.error?.stack}
            </pre>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-primary-500 text-white rounded hover:bg-primary-400"
            >
              Reload Page
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// Layouts
import { RootLayout } from './layouts/RootLayout';
import { ClientLayout } from './layouts/ClientLayout';

// Pages
import { UnderstandPage } from './routes/UnderstandPage';
import { ImplicationsPage } from './routes/ImplicationsPage';
import { DecidePage } from './routes/DecidePage';
import { LaunchPage } from './routes/LaunchPage';
import { RequestPage } from './routes/RequestPage';
import { ClientDashboard } from './routes/clients/ClientDashboard';
import { MarketPage } from './routes/clients/MarketPage';
import { PricingPage } from './routes/clients/PricingPage';
import { ClientLaunchPage } from './routes/clients/ClientLaunchPage';
import { PortfolioPage } from './routes/clients/PortfolioPage';
import { LibraryPage } from './routes/LibraryPage';
import { HomePage } from './routes/HomePage';
import { CompareConsumersPage } from './routes/CompareConsumersPage';
import { DataResearchPage } from './routes/DataResearchPage';

// Detail Pages
import {
  MarketLandscapeDetail,
  ConsumerIntelligenceDetail,
  CompetitivePositionDetail,
  PortfolioHealthDetail,
  PerformanceDiagnosisDetail,
  OpportunityAnalysisDetail,
  SensitivityAnalysisDetail,
} from './routes/DetailPages';

function App() {
  return (
    <ErrorBoundary>
      <LanguageProvider>
        <ClientProvider>
          <ScenarioProvider>
            <BrowserRouter>
            <Routes>
              <Route element={<RootLayout />}>
                <Route path="/" element={<HomePage />} />
                <Route path="/overview" element={<Navigate to="/" replace />} />
                <Route path="/understand" element={<UnderstandPage />} />
                <Route path="/understand/market-landscape" element={<MarketLandscapeDetail />} />
                <Route path="/understand/consumer-intelligence" element={<ConsumerIntelligenceDetail />} />
                <Route path="/understand/compare-consumers" element={<CompareConsumersPage />} />
                <Route path="/understand/competitive-position" element={<CompetitivePositionDetail />} />
                <Route path="/understand/portfolio-health" element={<PortfolioHealthDetail />} />
                <Route path="/implications" element={<ImplicationsPage />} />
                <Route path="/implications/performance-diagnosis" element={<PerformanceDiagnosisDetail />} />
                <Route path="/implications/opportunity-analysis" element={<OpportunityAnalysisDetail />} />
                <Route path="/implications/sensitivity-analysis" element={<SensitivityAnalysisDetail />} />
                <Route path="/decide" element={<DecidePage />} />
                <Route path="/decide/launch" element={<LaunchPage />} />
                <Route path="/library" element={<LibraryPage />} />
                <Route path="/data-research" element={<DataResearchPage />} />
                <Route path="/research" element={<RequestPage />} />

                <Route path="/clients/:clientId" element={<ClientLayout />}>
                  <Route index element={<ClientDashboard />} />
                  <Route path="market" element={<MarketPage />} />
                  <Route path="pricing" element={<PricingPage />} />
                  <Route path="launch" element={<ClientLaunchPage />} />
                  <Route path="portfolio" element={<PortfolioPage />} />
                </Route>
              </Route>
            </Routes>
            </BrowserRouter>
          </ScenarioProvider>
        </ClientProvider>
      </LanguageProvider>
    </ErrorBoundary>
  );
}

export default App;
